import React, { useCallback, useEffect } from 'react';
import {
  bool,
  func,
  node,
  number,
  object,
  objectOf,
  oneOf,
  oneOfType,
  shape,
  string,
} from 'prop-types';

import { decimalFormat, noop } from '@ajaibid/utils';
import { Input } from '@ajaibid/components';
import useField from './hooks/useField';

const FormNumber = (props) => {
  const {
    id,
    name,
    defaultValue,
    error: propsError,
    showError: propsShowError,
    onChange,
    onError,
    onValid,
    validators,
    ...baseProps
  } = props;

  const [
    fieldValue,
    { updateField, updateForm, error: errorMessage, errorKey, showError, setShowError },
  ] = useField(name, { defaultValue, validators });

  const onErrorCb = useCallback((params) => onError(params), [onError]);
  const onValidCb = useCallback((params) => onValid(params), [onValid]);

  useEffect(() => {
    if (errorMessage) {
      onErrorCb({ errorMessage, errorKey });
    } else {
      onValidCb();
    }
  }, [errorMessage, errorKey, onErrorCb, onValidCb]);

  // TODO: this line breaks validator. Wait for QA regression, if it works fine without it, remove
  // useEffect(() => {
  //   if (propsError) {
  //     updateForm(name, fieldValue, propsError);
  //   }
  // }, [propsError, name, updateForm]);

  const handleChange = (event) => {
    if (onChange) {
      // TODO: deprecate this libEvent
      const libEvent = {
        floatValue: event.target.value,
        value: decimalFormat(event.target.value),
        formattedValue: decimalFormat(event.target.value),
      };
      onChange(libEvent, { updateField, updateForm });
    } else {
      updateField(event.target.value);
    }
  };

  return (
    <Input.Number
      id={id}
      name={name}
      value={fieldValue}
      onChange={handleChange}
      onBlur={() => !showError && setShowError(true)}
      // Only pass the error message when Form showError is true.
      error={(showError || propsShowError) ? propsError || errorMessage || '' : ''}
      {...baseProps}
    />
  );
};

FormNumber.propTypes = {
  // <Input.Number> props
  id: string.isRequired,
  name: string.isRequired,
  append: oneOfType([string, node]),
  block: bool,
  bold: bool,
  className: string,
  decimalScale: number,
  decimalSeparator: string,
  disabled: bool,
  maxValue: number,
  minValue: number,
  placeholder: string,
  prepend: oneOfType([string, node]),
  size: string,
  style: object,
  textHint: string,
  thousandSeparator: oneOfType([string, bool]),
  variant: oneOf(['default', 'border-bottom']),
  borderColor: string,
  // <Form.Number> props
  defaultValue: number,
  error: string,
  onChange: oneOfType([func, bool]),
  onError: func,
  onValid: func,
  validators: objectOf(
    shape({
      validator: func,
      message: string,
    })
  ),
};

FormNumber.defaultProps = {
  // <Input.Number> props
  append: '',
  block: false,
  bold: false,
  className: '',
  decimalScale: 0,
  decimalSeparator: ',',
  disabled: false,
  maxValue: null,
  minValue: 0,
  placeholder: '',
  prepend: '',
  size: 'md',
  style: {},
  textHint: '',
  thousandSeparator: '.',
  variant: 'default',
  borderColor: '',
  // <Form.Number> props
  defaultValue: null,
  error: '',
  onChange: false,
  onError: noop,
  onValid: noop,
  validators: {},
};

export default FormNumber;
